import type { FC } from 'react';
import { Box } from '@gonfalon/observability-ui/components/Box';
import { Text } from '@gonfalon/observability-ui/components/Text';
import { themeVars } from '@gonfalon/observability-ui/theme';

const ERROR_COPY: Record<string, { title: string; body: string }> = {
  INFERENCE_UNAVAILABLE: {
    title: 'Vega is temporarily unavailable',
    body: "Vega's inference layer didn't respond. This is usually transient — try the same prompt again in a minute.",
  },
};

const DEFAULT_COPY = {
  title: 'Vega ran into a problem',
  body: 'Something went wrong while generating a response. Try again in a moment.',
};

/**
 * Inline banner rendered when autofix emits a typed `vega_error` envelope
 * (e.g. INFERENCE_UNAVAILABLE when the Bedrock proxy circuit breaker
 * trips). Replaces the indefinite "thinking..." spinner with an
 * actionable, human-readable explanation so the user knows to retry
 * instead of waiting forever.
 */
export const InferenceErrorMessage: FC<{
  errorCode?: string;
  errorMessage?: string;
}> = ({ errorCode, errorMessage }) => {
  const copy = errorCode && ERROR_COPY[errorCode] ? ERROR_COPY[errorCode] : DEFAULT_COPY;
  const body = errorMessage ?? copy.body;

  return (
    <Box
      marginBottom="12"
      padding="12"
      borderRadius="6"
      style={{
        border: `1px solid ${themeVars.interactive.fill.bad.enabled}`,
        backgroundColor: themeVars.static.surface.raised,
      }}
      role="status"
      aria-live="polite"
    >
      <Text size="small" weight="bold">
        {copy.title}
      </Text>
      <Box marginTop="4">
        <Text size="small" color="weak">
          {body}
        </Text>
      </Box>
    </Box>
  );
};
